import { useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { referencesAjax } from '../../ajax/ReferencesAjax';
import { DataTableFilteredRequest } from '../DataTable/TableHook';
import { ErrorResponse } from '../../DTOs/Common/ErrorResponse';
import { PagedResult } from '../../DTOs/Common/PagedResult';
import { PitStageDTO } from '../../DTOs/PitStageDTO';
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { MaterialTypeDTO } from "../../DTOs/MaterialTypeDTO";
import { MaterialTypeClassDTO } from "../../DTOs/MaterialTypeClassDTO";
import i18n from "../../i18n";

const LOOKUP_STALE_MS = 30 * 60 * 1000;
const LOOKUP_GC_MS = Number.POSITIVE_INFINITY;

export function useOptions<T, U>(
  queryKey: readonly unknown[],
  fetchFn: () => Promise<PagedResult<T> | T[] | ErrorResponse>,
  mapFn: (item: T) => U,
  opts?: {
    enabled?: boolean;
    onSuccessfulFetch?: (items: U[]) => void;
  }
) {
  const { data, isLoading } = useQuery<T[]>({
    queryKey,
    queryFn: async () => {
      const response = await fetchFn();
      if (ErrorResponse.isErrorResponse(response)) throw new Error(response.messages?.join(', ') || 'Failed to load options');
      return Array.isArray(response) ? response : response.data;
    },
    enabled: opts?.enabled ?? true,
    staleTime: LOOKUP_STALE_MS,
    gcTime: LOOKUP_GC_MS,
  });

  const mapFnRef = useRef(mapFn);
  mapFnRef.current = mapFn;

  const options = useMemo(() => (data ?? []).map(item => mapFnRef.current(item)), [data]);

  const onSuccessfulFetchRef = useRef(opts?.onSuccessfulFetch);
  onSuccessfulFetchRef.current = opts?.onSuccessfulFetch;
  useEffect(() => {
    if (data && data.length > 0) {
      onSuccessfulFetchRef.current?.(data.map(item => mapFnRef.current(item)));
    }
  }, [data]);

  return { options, isLoading };
}

export type MaterialTypeGroupOption = {
  label: string;
  value: number;
  group: string;
  materialTypeId: number;
  isWaste: boolean;
};

export function buildMaterialTypeGroupOptions(
  materialTypes: MaterialTypeDTO[],
  materialTypeClasses: MaterialTypeClassDTO[],
  opts?: { includeAllOption?: boolean }
): MaterialTypeGroupOption[] {
  return materialTypes.flatMap(t => {
    const classes = materialTypeClasses
      .filter(c => c.materialTypeId === t.id)
      .map(c => ({ label: c.class, value: c.id!, group: t.name!, materialTypeId: t.id!, isWaste: t.isWaste }));

    return opts?.includeAllOption
      ? [{ label: i18n.t("options.all"), value: -1, group: t.name!, materialTypeId: t.id!, isWaste: t.isWaste }, ...classes]
      : classes;
  });
}

export function useMaterialTypeGroupOptions(nonWasteOnly: boolean = false, opts?: { includeAllOption?: boolean }) {
  const { data: mt, isLoading: mtLoading } = useQuery({
    queryKey: ['references', 'materialTypes', { nonWasteOnly, isLive: true }],
    queryFn: async () => {
      const res = await referencesAjax.getMaterialTypes(
        (nonWasteOnly
          ? { isWaste: false, isLive: true, pageNumber: 1, pageSize: 100 }
          : { isLive: true, pageNumber: 1, pageSize: 100 }) as DataTableFilteredRequest
      );

      if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages?.join(', ') || 'Failed to load material types');
      return res.data;
    },
    staleTime: LOOKUP_STALE_MS,
    gcTime: LOOKUP_GC_MS,
  });

  const { data: mtc, isLoading: mtcLoading } = useQuery({
    queryKey: ['references', 'materialTypeClasses', { isLive: true }],
    queryFn: async () => {
      const res = await referencesAjax.getMaterialTypeClasses(
        { isLive: true, pageNumber: 1, pageSize: 100 } as DataTableFilteredRequest
      );
      if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages?.join(', ') || 'Failed to load material type classes');
      return res.data;
    },
    staleTime: LOOKUP_STALE_MS,
    gcTime: LOOKUP_GC_MS,
  });

  const grouped = useMemo(
    () => (mt && mtc ? buildMaterialTypeGroupOptions(mt, mtc, opts) : []),
    [mt, mtc, opts?.includeAllOption]
  );

  return { options: grouped, isLoading: mtLoading || mtcLoading };
}

export function usePitGroupOptions() {
  const { data: pits, isLoading: pitsLoading } = useQuery({
    queryKey: ['references', 'pits'],
    queryFn: async () => {
      const res = await referencesAjax.getPits({} as DataTableFilteredRequest);
      if (ErrorResponse.isErrorResponse(res)) return [] as LocationDTO[];
      return res.data;
    },
    staleTime: LOOKUP_STALE_MS,
    gcTime: LOOKUP_GC_MS,
  });

  const { data: pitStages, isLoading: pitStagesLoading } = useQuery({
    queryKey: ['references', 'pitStages'],
    queryFn: async () => {
      const res = await referencesAjax.getPitStages({} as DataTableFilteredRequest);
      if (ErrorResponse.isErrorResponse(res)) return [] as PitStageDTO[];
      return res.data;
    },
    staleTime: LOOKUP_STALE_MS,
    gcTime: LOOKUP_GC_MS,
  });

  const grouped = useMemo(() => {
    if (!pits || !pitStages) return [];
    return pits.flatMap(pit =>
      pitStages.filter(ps => ps.pitId === pit.id)
        .map(ps => ({
          label: ps.name!,
          value: ps.id!,
          group: pit.name!,
          pitId: pit.id!,
        }))
    );
  }, [pits, pitStages]);

  return { options: grouped, isLoading: pitsLoading || pitStagesLoading };
}
